import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getBackendUrl } from '../../services/origin';
import {
  Briefcase, LayoutDashboard, Users, DollarSign, Wallet, UserCog, LogOut,
  Copy, Link2, Loader2, CheckCircle2, Clock, Upload, Download, AlertCircle,
  TrendingUp, Send, Megaphone, FileText, Image as ImageIcon, Film, Package,
} from 'lucide-react';

const API = `${getBackendUrl()}/api`;

function agentApi() {
  const token = localStorage.getItem('agent_token');
  return axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });
}


export default function AgentPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agent_token');
    if (!token) {
      navigate('/agent/login');
      return;
    }
    const info = localStorage.getItem('agent_info');
    if (info) setAgent(JSON.parse(info));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_info');
    navigate('/agent/login');
  };

  if (!agent) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const NAV = [
    { k: 'dashboard', l: 'Dashboard', i: LayoutDashboard },
    { k: 'referrals', l: 'Iscrizioni', i: Users },
    { k: 'commissions', l: 'Commissioni', i: DollarSign },
    { k: 'payouts', l: 'Payout', i: Wallet },
    { k: 'marketing', l: 'Marketing', i: Megaphone },
    { k: 'profile', l: 'Profilo', i: UserCog },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="agent-portal">
      {/* Sidebar */}
      <aside className="w-56 bg-[#1A1A1A] text-white flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#D44E20] flex items-center justify-center">
              <Briefcase size={16} className="text-white" />
            </div>
            <span className="font-bold text-lg">Lazo Agents</span>
          </div>
          <p className="text-xs text-white/60">{agent.name}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ k, l, i: Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`agent-nav-${k}`}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                tab === k ? 'bg-[#D44E20] text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} data-testid="agent-logout"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/60 hover:bg-red-500/20 hover:text-red-400">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {tab === 'dashboard' && <DashboardTab />}
        {tab === 'referrals' && <ReferralsTab />}
        {tab === 'commissions' && <CommissionsTab />}
        {tab === 'payouts' && <PayoutsTab />}
        {tab === 'marketing' && <MarketingTab />}
        {tab === 'profile' && <ProfileTab />}
      </main>
    </div>
  );
}


function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { agentApi().get('/agent/dashboard').then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const copyLink = () => {
    const url = `${window.location.origin}/signup?ref=${data.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiato');
  };
  const copyCode = () => {
    navigator.clipboard.writeText(data.referral_code);
    toast.success('Codice copiato');
  };

  return (
    <div className="p-8" data-testid="agent-dashboard-tab">
      <h1 className="text-3xl font-bold mb-2">Ciao {data.name} 👋</h1>
      <p className="text-sm text-slate-500 mb-6">Benvenuto nel tuo portale agente Lazo.</p>

      {/* Referral share card */}
      <div className="bg-gradient-to-br from-[#D44E20] to-[#b33d16] text-white rounded-2xl p-6 mb-6" data-testid="referral-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white/70 mb-1">Il tuo codice referral</p>
            <p className="text-3xl font-bold font-mono" data-testid="referral-code">{data.referral_code}</p>
          </div>
          <Link2 size={24} className="text-white/70" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyCode} data-testid="copy-code-btn"
            className="bg-white/15 hover:bg-white/25 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 backdrop-blur">
            <Copy size={13} /> Copia codice
          </button>
          <button onClick={copyLink} data-testid="copy-link-btn"
            className="bg-white text-[#D44E20] hover:shadow-md px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5">
            <Link2 size={13} /> Copia link (?ref={data.referral_code})
          </button>
        </div>
        <p className="text-xs text-white/80 mt-3">
          Condividi il link o il codice: chi si iscrive tramite te attiva una commissione per te.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="agent-kpi-signups">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Iscrizioni totali</p>
          <p className="text-2xl font-bold">{data.referrals_count}</p>
          <p className="text-xs text-slate-500 mt-1">{data.active_referrals} attivi</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="agent-kpi-earned">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Maturato</p>
          <p className="text-2xl font-bold text-[#D44E20]">{data.available_for_payout.toFixed(2)} €</p>
          <p className="text-xs text-slate-500 mt-1">Disponibile per payout</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">In attesa pagamento</p>
          <p className="text-2xl font-bold">{data.commissions_by_status.requested.toFixed(2)} €</p>
          <p className="text-xs text-slate-500 mt-1">Richiesta già inviata</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Pagato totale</p>
          <p className="text-2xl font-bold text-emerald-600">{data.commissions_by_status.paid.toFixed(2)} €</p>
        </div>
      </div>

      {/* Commission structure */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-[#D44E20]" />
          <h3 className="text-sm font-bold">La tua commissione</h3>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-slate-500 mb-1">Upfront primo pagamento</p>
            <p className="text-xl font-bold text-[#1A1A1A]">{data.upfront_amount} €</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">% ricorrente</p>
            <p className="text-xl font-bold text-[#1A1A1A]">{data.recurring_rate} %</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Soglia minima payout</p>
            <p className="text-xl font-bold text-[#1A1A1A]">{data.payout_threshold} €</p>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Per ogni brand iscritto tramite te ricevi il bonus upfront al primo pagamento più la % ricorrente su ogni pagamento successivo.
        </p>
      </div>
    </div>
  );
}


function ReferralsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    agentApi().get('/agent/referrals').then(r => setList(r.data.referrals || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const statusChip = (s) => {
    const map = {
      signed_up: { bg: '#FEF3C7', fg: '#92400E', label: 'Iscritto (non attivato)' },
      active: { bg: '#D1FAE5', fg: '#065F46', label: 'Attivo' },
      churned: { bg: '#FEE2E2', fg: '#991B1B', label: 'Disattivato' },
    };
    const v = map[s] || { bg: '#E2E8F0', fg: '#475569', label: s };
    return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: v.bg, color: v.fg }}>{v.label}</span>;
  };

  return (
    <div className="p-8" data-testid="agent-referrals-tab">
      <h1 className="text-2xl font-bold mb-6">Brand iscritti tramite te ({list.length})</h1>
      {list.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <Users size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Nessuna iscrizione ancora. Condividi il tuo link/codice!</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Brand</th>
                <th className="text-left px-4 py-3">Data iscrizione</th>
                <th className="text-left px-4 py-3">Metodo</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Commissioni generate</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold">{r.brand_name}</td>
                  <td className="px-4 py-3 text-slate-600">{new Date(r.signup_date).toLocaleDateString('it-IT')}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.source === 'link' ? 'Link' : 'Codice'}</td>
                  <td className="px-4 py-3">{statusChip(r.status)}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#D44E20]">{r.total_commission.toFixed(2)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CommissionsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    agentApi().get('/agent/commissions').then(r => setList(r.data.commissions || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const statusChip = (s) => {
    const map = {
      earned: { bg: '#D1FAE5', fg: '#065F46', label: 'Maturata' },
      requested: { bg: '#DBEAFE', fg: '#1E40AF', label: 'Richiesta' },
      paid: { bg: '#E0E7FF', fg: '#3730A3', label: 'Pagata' },
      pending: { bg: '#FEF3C7', fg: '#92400E', label: 'In attesa' },
      cancelled: { bg: '#FEE2E2', fg: '#991B1B', label: 'Annullata' },
    };
    const v = map[s] || { bg: '#E2E8F0', fg: '#475569', label: s };
    return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: v.bg, color: v.fg }}>{v.label}</span>;
  };

  return (
    <div className="p-8" data-testid="agent-commissions-tab">
      <h1 className="text-2xl font-bold mb-6">Commissioni ({list.length})</h1>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-10 bg-white rounded-2xl border border-slate-200">Nessuna commissione ancora.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Brand</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-right px-4 py-3">Base</th>
                <th className="text-right px-4 py-3">%</th>
                <th className="text-right px-4 py-3">Commissione</th>
                <th className="text-left px-4 py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {list.map(c => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-xs">{new Date(c.created_at).toLocaleDateString('it-IT')}</td>
                  <td className="px-4 py-3 font-semibold">{c.brand_name}</td>
                  <td className="px-4 py-3 text-xs">{c.type === 'upfront' ? 'Upfront' : 'Ricorrente'}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{Number(c.base_amount).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-right text-slate-600">{c.type === 'recurring' ? `${c.rate}%` : '—'}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#D44E20]">{Number(c.amount).toFixed(2)} €</td>
                  <td className="px-4 py-3">{statusChip(c.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function PayoutsTab() {
  const [dashboard, setDashboard] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      agentApi().get('/agent/dashboard'),
      agentApi().get('/agent/payouts'),
    ]).then(([d, p]) => {
      setDashboard(d.data);
      setList(p.data.payouts || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const requestPayout = async () => {
    try {
      await agentApi().post('/agent/payouts');
      toast.success('Richiesta payout creata — carica ora la fattura');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const downloadProforma = async (p) => {
    try {
      const r = await agentApi().get(`/agent/payouts/${p.id}/proforma-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `proforma_${p.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const statusChip = (s) => {
    const map = {
      pending: { bg: '#FEF3C7', fg: '#92400E', label: 'In attesa fattura/approvazione' },
      approved: { bg: '#DBEAFE', fg: '#1E40AF', label: 'Approvato — in pagamento' },
      paid: { bg: '#D1FAE5', fg: '#065F46', label: 'Pagato' },
      rejected: { bg: '#FEE2E2', fg: '#991B1B', label: 'Rifiutato' },
    };
    const v = map[s] || { bg: '#E2E8F0', fg: '#475569', label: s };
    return <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: v.bg, color: v.fg }}>{v.label}</span>;
  };

  return (
    <div className="p-8" data-testid="agent-payouts-tab">
      <h1 className="text-2xl font-bold mb-6">Payout</h1>

      {/* Request card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Disponibile</p>
            <p className="text-3xl font-bold text-[#D44E20]">{dashboard.available_for_payout.toFixed(2)} €</p>
            <p className="text-xs text-slate-500 mt-1">Soglia minima payout: {dashboard.payout_threshold} €</p>
          </div>
          <button onClick={requestPayout} disabled={!dashboard.can_request_payout}
            data-testid="request-payout-btn"
            className="px-5 py-3 bg-[#D44E20] text-white rounded-lg font-bold hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
            <Send size={15} /> Richiedi payout
          </button>
        </div>
        {!dashboard.can_request_payout && dashboard.available_for_payout > 0 && (
          <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
            <AlertCircle size={12} /> Non hai ancora raggiunto la soglia minima.
          </p>
        )}
      </div>

      <h3 className="text-lg font-bold mb-3">Storico richieste</h3>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-10 bg-white rounded-2xl border border-slate-200">Nessuna richiesta di payout.</p>
      ) : (
        <div className="space-y-3">
          {list.map(p => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`payout-item-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xl font-bold text-[#D44E20]">{Number(p.total_amount).toFixed(2)} €</p>
                    {statusChip(p.status)}
                  </div>
                  <p className="text-xs text-slate-500">
                    Richiesto: {new Date(p.requested_at).toLocaleDateString('it-IT')}
                    {p.paid_at && ` · Pagato: ${new Date(p.paid_at).toLocaleDateString('it-IT')}`}
                  </p>
                  {p.payment_transaction_ref && <p className="text-xs text-slate-500">CRO: {p.payment_transaction_ref}</p>}
                  {p.rejected_reason && <p className="text-xs text-red-600 mt-1">Motivo rifiuto: {p.rejected_reason}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => downloadProforma(p)} data-testid={`download-proforma-${p.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#1A1A1A] font-semibold flex items-center gap-1">
                    <Download size={11} /> Proforma
                  </button>
                  {p.status === 'pending' && !p.invoice_pdf_path && (
                    <button onClick={() => setUploadTarget(p)} data-testid={`upload-invoice-${p.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-[#D44E20] text-white font-semibold flex items-center gap-1 hover:shadow-md">
                      <Upload size={11} /> Carica fattura
                    </button>
                  )}
                  {p.invoice_pdf_path && <span className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 size={11} /> Fattura {p.invoice_number}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadTarget && <UploadInvoiceModal payout={uploadTarget} onClose={() => setUploadTarget(null)} onDone={load} />}
    </div>
  );
}


function UploadInvoiceModal({ payout, onClose, onDone }) {
  const [pdf, setPdf] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!pdf || !invoiceNumber) {
      toast.error('Carica PDF e inserisci il numero fattura');
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append('pdf', pdf);
    fd.append('invoice_number', invoiceNumber);
    fd.append('invoice_date', invoiceDate);
    try {
      await agentApi().post(`/agent/payouts/${payout.id}/upload-invoice`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fattura caricata! Riceverai il pagamento dopo l\'approvazione.');
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="upload-invoice-modal">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold">Carica la tua fattura</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            Scarica prima la <b>proforma</b> dal payout per avere tutti i dati. Usa il tuo cassetto fiscale (o Fatture in Cloud, Aruba, ecc.) per emettere la fattura fiscale verso <b>Lazo</b>, poi caricala qui.
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Numero fattura*</label>
            <input type="text" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
              data-testid="invoice-number-input"
              placeholder="Es. 2026/001"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Data fattura*</label>
            <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">PDF fattura*</label>
            <input type="file" accept="application/pdf" onChange={e => setPdf(e.target.files?.[0])}
              data-testid="invoice-pdf-input"
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:font-semibold hover:file:bg-slate-200" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="upload-invoice-confirm"
            className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={13} /> Invia</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function ProfileTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    agentApi().get('/agent/me').then(r => setForm(r.data));
  }, []);

  if (!form) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (password) payload.password = password;
      await agentApi().put('/agent/me', payload);
      toast.success('Profilo aggiornato');
      setPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSaving(false);
  };

  const field = (key, label, type = 'text') => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input type={type} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
        data-testid={`profile-field-${key}`}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
    </div>
  );

  return (
    <div className="p-8 max-w-3xl" data-testid="agent-profile-tab">
      <h1 className="text-2xl font-bold mb-6">Il mio profilo</h1>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          {field('name', 'Nome completo')}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
            <input disabled value={form.email} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
          </div>
          {field('phone', 'Telefono', 'tel')}
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Dati fiscali</p>
          <div className="grid grid-cols-2 gap-4">
            {field('vat_number', 'P.IVA')}
            {field('tax_code', 'Codice fiscale')}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Regime fiscale</label>
              <select value={form.tax_regime} onChange={e => setForm({ ...form, tax_regime: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="ordinario">Ordinario</option>
                <option value="forfettario">Forfettario</option>
                <option value="occasional">Prestazione occasionale</option>
              </select>
            </div>
            {field('iban', 'IBAN (per bonifici)')}
          </div>
          <div className="mt-3">{field('address', 'Indirizzo')}</div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sicurezza</p>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Nuova password (lascia vuoto per non cambiare)</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            data-testid="profile-new-password"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="save-profile"
            className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}



function categoryIcon(cat) {
  const m = {
    'Brochure': FileText,
    'Social Media': ImageIcon,
    'Presentazioni': FileText,
    'Listino prezzi': FileText,
    'Video': Film,
    'Altro': Package,
  };
  return m[cat] || Package;
}

function formatSize(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}


function MarketingTab() {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');

  useEffect(() => {
    agentApi().get('/agent/marketing-assets')
      .then(r => {
        setAssets(r.data.assets || []);
        setCategories(r.data.categories || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const download = async (a) => {
    try {
      const r = await agentApi().get(`/agent/marketing-assets/${a.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: a.content_type || 'application/octet-stream' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = a.file_name || a.title;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (e) {
      toast.error('Errore download');
    }
  };

  const copyShareLink = (a) => {
    if (!a.share_url) {
      toast.error('Link non disponibile');
      return;
    }
    navigator.clipboard.writeText(a.share_url);
    toast.success('Link copiato! Condividilo con il cliente.');
  };

  const filtered = catFilter ? assets.filter(a => a.category === catFilter) : assets;

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  return (
    <div className="p-8" data-testid="agent-marketing-tab">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Materiali Marketing</h1>
        <p className="text-sm text-slate-500">
          Scarica o condividi questi asset con i tuoi clienti finali per presentare Lazo.
        </p>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            onClick={() => setCatFilter('')}
            data-testid="cat-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              catFilter === '' ? 'bg-[#D44E20] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#D44E20]'
            }`}
          >
            Tutti ({assets.length})
          </button>
          {categories.map(c => {
            const count = assets.filter(a => a.category === c).length;
            if (count === 0) return null;
            return (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                data-testid={`cat-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  catFilter === c ? 'bg-[#D44E20] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#D44E20]'
                }`}
              >
                {c} ({count})
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="empty-marketing">
          <Megaphone size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Nessun asset disponibile in questa categoria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(a => {
            const Icon = categoryIcon(a.category);
            return (
              <div
                key={a.id}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
                data-testid={`agent-asset-${a.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-lg bg-[#FDECE7] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#D44E20]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#1A1A1A] leading-tight">{a.title}</p>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">{a.category}</p>
                  </div>
                </div>
                {a.description && (
                  <p className="text-xs text-slate-600 mb-3 line-clamp-2">{a.description}</p>
                )}
                <p className="text-[11px] text-slate-400 mb-3">{formatSize(a.size_bytes)} · {a.file_name}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => download(a)}
                    data-testid={`download-${a.id}`}
                    className="flex-1 px-3 py-2 bg-[#D44E20] text-white rounded-lg text-xs font-semibold hover:shadow-md flex items-center justify-center gap-1.5"
                  >
                    <Download size={12} /> Scarica
                  </button>
                  <button
                    onClick={() => copyShareLink(a)}
                    data-testid={`share-${a.id}`}
                    className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-[#1A1A1A] rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                  >
                    <Link2 size={12} /> Condividi
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
